import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ExternalLink, ChevronLeft, ChevronRight, ChevronDown, Search, Scale, FileText, Gavel, Image as ImageIcon, Table2, Pencil } from "lucide-react";
import { getToken } from "@/adminAuth";
import ImageMap from "@/components/ImageMap";

const API = `/api`;

const TYPE_ICONS = { concepts: Scale, clauses: FileText, procedures: Gavel };

export default function FicheDetail() {
  const { type, slug } = useParams();
  const navigate = useNavigate();
  const [fiche, setFiche] = useState(null);
  const [status, setStatus] = useState("loading");
  const [panelOpen, setPanelOpen] = useState(false);
  const [plancheState, setPlancheState] = useState(null);
  const [tableauxOpen, setTableauxOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [relatedAi, setRelatedAi] = useState(null);
  const [openSet, setOpenSet] = useState(() => new Set([0]));

  useEffect(() => {
    setStatus("loading");
    setPlancheState(null);
    setTableauxOpen(false);
    setOpenSet(new Set([0]));
    fetch(`${API}/knowledge/${type}/${slug}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        setFiche(data);
        setStatus(data ? "ok" : "notfound");
      })
      .catch(() => setStatus("notfound"));
  }, [type, slug]);

  useEffect(() => {
    if (fiche) {
      const url = `https://www.e2.law/ressources/${type}/${slug}`;
      const desc = fiche.excerpt || "";
      document.title = `${fiche.seo_title || fiche.title} — e².law`;
      const setMeta = (selector, attr, value) => {
        if (!value) return;
        let el = document.querySelector(selector);
        if (!el) {
          el = document.createElement("meta");
          const [kind, name] = selector.replace("meta[", "").replace("]", "").split("=");
          el.setAttribute(kind, name.replace(/"/g, ""));
          document.head.appendChild(el);
        }
        el.setAttribute(attr, value);
      };
      setMeta('meta[name="description"]', "content", desc);
      setMeta('meta[property="og:title"]', "content", fiche.title);
      setMeta('meta[property="og:description"]', "content", desc);
      setMeta('meta[property="og:url"]', "content", url);
      setMeta('meta[property="og:type"]', "content", "article");
      setMeta('meta[name="twitter:title"]', "content", fiche.title);
      setMeta('meta[name="twitter:description"]', "content", desc);
      let canon = document.querySelector('link[rel="canonical"]');
      if (!canon) {
        canon = document.createElement("link");
        canon.setAttribute("rel", "canonical");
        document.head.appendChild(canon);
      }
      canon.setAttribute("href", url);
      // Donnees structurees Schema.org (Article) : autorite citee par les moteurs et IA
      let ld = document.getElementById("ld-fiche");
      if (!ld) {
        ld = document.createElement("script");
        ld.type = "application/ld+json";
        ld.id = "ld-fiche";
        document.head.appendChild(ld);
      }
      ld.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        headline: fiche.title,
        description: desc,
        mainEntityOfPage: url,
        author: { "@type": "Organization", name: "e².law", url: "https://www.e2.law" },
        publisher: { "@type": "Organization", name: "e².law", url: "https://www.e2.law" },
        keywords: (fiche.tags || []).join(", "),
        ...(fiche.source_published ? { datePublished: fiche.source_published } : {}),
        ...(fiche.edited_at || fiche.source_modified
          ? { dateModified: fiche.edited_at || fiche.source_modified }
          : {}),
      });
    }
    return () => {
      document.title = "e².law — Cabinet d'avocats d'affaires · Bruxelles";
      const canon = document.querySelector('link[rel="canonical"]');
      if (canon) canon.setAttribute("href", "https://www.e2.law/");
      document.getElementById("ld-fiche")?.remove();
    };
  }, [fiche, type, slug]);

  useEffect(() => {
    if (status !== "ok") return;
    setRelatedAi(null);
    fetch(`${API}/knowledge/${type}/${slug}/related`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setRelatedAi)
      .catch(() => setRelatedAi([]));
  }, [status, type, slug]);

  const related = relatedAi || [];
  const aiProposed = relatedAi && relatedAi.length > 0;

  const submitSearch = (e) => {
    e.preventDefault();
    if (query.trim()) navigate(`/ressources?q=${encodeURIComponent(query.trim())}`);
  };

  const Icon = fiche ? TYPE_ICONS[fiche.type] || Scale : Scale;

  const fieldSections = fiche?.fields?.length
    ? Object.entries(
        fiche.fields
          .filter((f) => f.section !== "Note de rédaction")
          .reduce((acc, f) => {
            (acc[f.section] = acc[f.section] || []).push(f);
            return acc;
          }, {})
      )
    : [];

  const RELATION_KINDS = {
    peut_introduire: "Peut introduire",
    demandeur: "Peut être introduit par",
    defendeur: "Peut être introduit contre",
    partie: "A pour partie",
    base_legale: "Base légale",
    est_partie_de: "Fait partie de",
    est_oppose_a: "Est opposé à",
    obligation_pour: "Obligation pour",
    lie_a: "Lié à",
    peut_faire_usage_de: "Peut faire usage de",
    peut_etre_utilise_par: "Peut être utilisé par",
  };
  const RELATION_INVERSE = {
    peut_introduire: "Peut être introduit par",
    demandeur: "Peut introduire",
    defendeur: "Peut faire l'objet de",
    partie: "A pour partie",
    base_legale: "Base légale de",
    est_partie_de: "Comprend",
    est_oppose_a: "Est opposé à",
    obligation_pour: "Obligation pour",
    lie_a: "Lié à",
    peut_faire_usage_de: "Peut être utilisé par",
    peut_etre_utilise_par: "Peut faire usage de",
    mention: "Citée dans",
  };
  const relationEntries = [
    ...(fiche?.relations || []).map((r) => ({ ...r, label: RELATION_KINDS[r.kind] || r.kind })),
    ...(fiche?.incoming_relations || []).map((r) => ({ ...r, label: RELATION_INVERSE[r.kind] || r.kind })),
  ];
  // Dédoublonne les miroirs : quand les deux fiches déclarent le lien
  // (ex. « lié à » dans les deux sens), ne l'afficher qu'une fois.
  const seenRel = new Set();
  const dedupedEntries = relationEntries.filter((r) => {
    const key = `${r.label}|${r.type}|${r.slug}`;
    if (seenRel.has(key)) return false;
    seenRel.add(key);
    return true;
  });
  const relationGroups = dedupedEntries.length
    ? Object.entries(
        dedupedEntries.reduce((acc, r) => {
          (acc[r.label] = acc[r.label] || []).push(r);
          return acc;
        }, {})
      )
    : [];
  const plancheShown = fiche
    ? plancheState === null
      ? !!fiche.show_planche
      : plancheState === "shown"
    : false;

  // Longues fiches (>= 3 titres et >= 6000 signes) : decoupage en sections repliables.
  // Niveau de decoupe : H1 si le document en contient plusieurs (collage Word), sinon H2.
  // Les conteneurs WordPress (divs imbriquees) sont traverses pour trouver les titres.
  // Le contenu reste dans le DOM (replie en CSS) pour ne pas penaliser le referencement.
  const parsed = useMemo(() => {
    if (!fiche?.content_html) return null;
    const html = fiche.content_html;
    if (html.length < 6000) return null;
    const doc = new DOMParser().parseFromString(html, "text/html");
    const level = doc.body.querySelectorAll("h1").length >= 2 ? "h1" : "h2";
    if (doc.body.querySelectorAll(level).length < 3) return null;
    const intro = [];
    const items = [];
    let current = null;
    const push = (node) => {
      const outer = node.nodeType === 1 ? node.outerHTML : node.textContent;
      if (current) current.html += outer;
      else intro.push(outer);
    };
    const walk = (parent) => {
      parent.childNodes.forEach((node) => {
        if (node.nodeType === 1 && node.tagName.toLowerCase() === level) {
          current = { title: node.textContent.trim(), html: "" };
          items.push(current);
        } else if (node.nodeType === 1 && node.querySelector && node.querySelector(level)) {
          walk(node);
        } else {
          push(node);
        }
      });
    };
    walk(doc.body);
    if (items.length < 3) return null;
    return { intro: intro.join(""), items };
  }, [fiche?.content_html]);

  const toggleSection = (i) =>
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  const allOpen = parsed ? openSet.size === parsed.items.length : false;

  return (
    <div className="pt-40 pb-24 sm:pb-32 min-h-screen" data-testid="fiche-page">
      <div className="max-w-3xl mx-auto px-6 sm:px-12 lg:px-20">
        <Link
          to="/ressources"
          data-testid="fiche-back-link"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#A0A5B1] hover:text-gold transition-colors duration-300"
        >
          <ArrowLeft className="w-4 h-4" /> Retour aux ressources
        </Link>

        {status === "loading" && (
          <p className="mt-20 text-[#626773] font-mono text-sm" data-testid="fiche-loading">Chargement…</p>
        )}

        {status === "notfound" && (
          <p className="mt-20 font-serif text-2xl" data-testid="fiche-notfound">Fiche introuvable.</p>
        )}

        {status === "ok" && fiche && getToken() && (
          <Link
            to={`/admin/fiches/${type}/${slug}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="fiche-admin-edit-link"
            title="Modifier cette fiche dans le back-office"
            className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 bg-gold text-ink font-mono text-[11px] uppercase tracking-[0.15em] px-4 py-2.5 shadow-lg shadow-black/40 hover:bg-gold-light transition-colors"
          >
            <Pencil className="w-3.5 h-3.5" /> Modifier
          </Link>
        )}

        {status === "ok" && fiche && (
          <motion.article
            key={fiche.slug}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <span className="font-mono text-[10px] tracking-[0.25em] text-gold uppercase border border-gold/25 px-3 py-1.5" data-testid="fiche-type-badge">
                {fiche.type_label}
              </span>
              {fiche.source_published && (
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#626773] font-mono" data-testid="fiche-published">
                  Publié le {new Date(fiche.source_published).toLocaleDateString("fr-BE", { day: "numeric", month: "long", year: "numeric" })}
                </span>
              )}
            </div>

            <h1 className="mt-6 font-serif font-light tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight" data-testid="fiche-title">
              {fiche.title}
            </h1>

            <div className="mt-8 gold-hairline" />

            {fiche.image && (
              <button
                onClick={() => setPlancheState(plancheShown ? "hidden" : "shown")}
                data-testid="fiche-planche-toggle"
                className="mt-10 inline-flex items-center gap-3 border border-gold/25 text-gold/80 text-xs uppercase tracking-[0.2em] px-5 py-3 hover:bg-gold/10 hover:text-gold transition-colors duration-300"
              >
                <ImageIcon className="w-4 h-4" />
                {plancheShown ? "Masquer la planche" : "Une planche illustre cette fiche — l'afficher"}
              </button>
            )}

            {fiche.tableaux && fiche.tableaux.length > 0 && (
              <button
                onClick={() => setTableauxOpen(!tableauxOpen)}
                data-testid="fiche-tableau-toggle"
                className="mt-10 ml-0 sm:ml-4 inline-flex items-center gap-3 border border-gold/25 text-gold/80 text-xs uppercase tracking-[0.2em] px-5 py-3 hover:bg-gold/10 hover:text-gold transition-colors duration-300"
              >
                <Table2 className="w-4 h-4" />
                {tableauxOpen
                  ? "Masquer le tableau explicatif"
                  : "Un tableau explicatif accompagne cette fiche — l'afficher"}
              </button>
            )}

            <AnimatePresence>
              {fiche.tableaux && tableauxOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="mt-6 space-y-10"
                  data-testid="fiche-tableaux"
                >
                  {fiche.tableaux.map((tb) => (
                    <div key={tb.id}>
                      {tb.title && (
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold/70">{tb.title}</p>
                      )}
                      {tb.form === "steps" ? (
                        <ol className="mt-4 border-l border-gold/25 ml-2" data-testid={`fiche-tableau-steps-${tb.id}`}>
                          {(tb.rows || []).map((row, i) => (
                            <li key={i} className="relative pl-8 pb-8 last:pb-0">
                              <span className="absolute -left-[11px] top-0 w-[22px] h-[22px] bg-gold text-ink font-mono text-[10px] flex items-center justify-center">
                                {i + 1}
                              </span>
                              <p className="font-serif text-lg text-[#FAF8F5] leading-snug">{row[0]}</p>
                              {row[1] && (
                                <p className="mt-1.5 text-sm text-[#A0A5B1] leading-relaxed">{row[1]}</p>
                              )}
                            </li>
                          ))}
                        </ol>
                      ) : (
                        <div className="mt-4 overflow-x-auto border border-white/10 bg-ink-card" data-testid={`fiche-tableau-table-${tb.id}`}>
                          <table className="w-full border-collapse">
                            <thead>
                              <tr className="bg-white/[0.03]">
                                {(tb.columns || []).map((c, ci) => (
                                  <th key={ci} className="px-5 py-3 text-left font-mono text-[10px] uppercase tracking-[0.2em] text-gold/80">
                                    {c}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                              {(tb.rows || []).map((row, ri) => (
                                <tr key={ri}>
                                  {row.map((cell, ci) => (
                                    <td key={ci} className="px-5 py-3.5 text-sm text-[#A0A5B1] leading-relaxed align-top">
                                      {cell}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {fiche.image && plancheShown && (
                <motion.figure
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className={`mt-6 border border-gold/25 bg-[#F5F0E6] p-4 sm:p-8 ${fiche.show_planche ? "" : "max-w-sm"}`}
                  data-testid="fiche-planche"
                >
                  <img
                    src={fiche.image}
                    alt={`Planche illustrative — ${fiche.title}`}
                    className="w-full h-auto"
                    loading="lazy"
                  />
                  <figcaption className="mt-4 flex items-center justify-between gap-4 flex-wrap">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9A7B3E]">
                      Planche e².law — {fiche.title}
                    </span>
                    <button
                      onClick={() => setPlancheState("hidden")}
                      data-testid="fiche-planche-hide"
                      className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9A7B3E]/70 hover:text-[#9A7B3E] transition-colors"
                    >
                      Masquer
                    </button>
                  </figcaption>
                </motion.figure>
              )}
            </AnimatePresence>

            {parsed ? (
              <div className="mt-10" data-testid="fiche-sections">
                {parsed.intro.trim() && (
                  <div className="fiche-content" dangerouslySetInnerHTML={{ __html: parsed.intro }} />
                )}
                <div className="mt-8 mb-2 flex items-center justify-between gap-4">
                  <span className="label-accent">{parsed.items.length} sections</span>
                  <button
                    onClick={() => setOpenSet(allOpen ? new Set() : new Set(parsed.items.map((_, i) => i)))}
                    data-testid="fiche-sections-toggle-all"
                    className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold/80 hover:text-gold transition-colors"
                  >
                    {allOpen ? "Tout replier" : "Tout déplier"}
                  </button>
                </div>
                <div className="divide-y divide-white/10 border-y border-white/10">
                  {parsed.items.map((s, i) => {
                    const open = openSet.has(i);
                    return (
                      <div key={i}>
                        <button
                          onClick={() => toggleSection(i)}
                          aria-expanded={open}
                          data-testid={`fiche-section-toggle-${i}`}
                          className="w-full flex items-center justify-between gap-4 py-5 text-left group"
                        >
                          <span className="font-serif text-xl sm:text-2xl leading-snug text-[#FAF8F5] group-hover:text-gold-light transition-colors duration-300">
                            {s.title}
                          </span>
                          <ChevronDown
                            className={`w-5 h-5 shrink-0 text-gold transition-transform duration-300 ${open ? "rotate-180" : ""}`}
                          />
                        </button>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateRows: open ? "1fr" : "0fr",
                            transition: "grid-template-rows 0.45s cubic-bezier(0.22, 1, 0.36, 1)",
                          }}
                        >
                          <div className="overflow-hidden">
                            <div
                              className="fiche-content pb-6"
                              data-testid={`fiche-section-body-${i}`}
                              dangerouslySetInnerHTML={{ __html: s.html }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div
                className="fiche-content mt-10"
                data-testid="fiche-content"
                dangerouslySetInnerHTML={{ __html: fiche.content_html }}
              />
            )}

            {fiche.imagemap && <ImageMap data={fiche.imagemap} />}

            {fieldSections.length > 0 && (
              <div className="mt-14" data-testid="fiche-fields">
                <span className="label-accent">Fiche technique</span>
                <div className="mt-6 space-y-10">
                  {fieldSections.map(([section, rows]) => (
                    <div key={section}>
                      <h3 className="font-serif text-xl text-gold-light">{section}</h3>
                      <div className="mt-4 border border-white/10 bg-ink-card divide-y divide-white/5">
                        {rows.map((f, i) =>
                          f.kind === "table" ? (
                            <div key={i} className="px-5 py-4" data-testid={`fiche-field-${section}-${i}`}>
                              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold/80 mb-3">
                                {f.label}
                              </p>
                              <div
                                className="fiche-content overflow-x-auto text-sm text-[#A0A5B1]"
                                dangerouslySetInnerHTML={{ __html: f.html }}
                              />
                            </div>
                          ) : f.html.includes("<details") ? (
                            <div key={i} className="px-5 py-4" data-testid={`fiche-field-${section}-${i}`}>
                              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold/80 mb-3">
                                {f.label}
                              </p>
                              <div
                                className="fiche-content text-sm text-[#A0A5B1] leading-relaxed"
                                dangerouslySetInnerHTML={{ __html: f.html }}
                              />
                            </div>
                          ) : (
                          <div key={i} className="grid sm:grid-cols-[240px_1fr]" data-testid={`fiche-field-${section}-${i}`}>
                            <div className="px-5 py-4 font-mono text-[10px] uppercase tracking-[0.2em] text-gold/80">
                              {f.label}
                            </div>
                            {f.kind === "badges" ? (
                              <div className="px-5 py-4 flex flex-wrap gap-2">
                                {f.html.split(", ").map((b) => (
                                  <span key={b} className="text-xs text-[#A0A5B1] border border-white/15 px-3 py-1">
                                    {b}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <div
                                className="fiche-content px-5 py-4 text-sm text-[#A0A5B1] leading-relaxed"
                                dangerouslySetInnerHTML={{ __html: f.html }}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {relationGroups.length > 0 && (
              <div className="mt-14" data-testid="fiche-relations">
                <span className="label-accent">Relations</span>
                <div className="mt-6 space-y-6">
                  {relationGroups.map(([label, rows]) => (
                    <div key={label}>
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#626773]">
                        {label}
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {rows.map((r, i) => (
                          <Link
                            key={`${r.type}-${r.slug}-${i}`}
                            to={`/ressources/${r.type}/${r.slug}`}
                            data-testid={`fiche-relation-${r.slug}`}
                            className="text-xs text-gold/80 border border-gold/25 px-3 py-1.5 hover:bg-gold hover:text-ink transition-colors"
                          >
                            {r.title || r.slug}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {related.length > 0 && (
              <div className="mt-16" data-testid="fiche-related">
                <div className="flex items-center gap-4 flex-wrap">
                  <span className="label-accent">Sujets liés</span>
                  {aiProposed && !fiche.related_validated && (
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold/60 border border-gold/20 px-2.5 py-1" data-testid="related-ai-badge">
                      proposés par IA — à valider
                    </span>
                  )}
                </div>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {related.map((r) => (
                    <Link
                      key={`${r.type}-${r.slug}`}
                      to={`/ressources/${r.type}/${r.slug}`}
                      data-testid={`related-${r.slug}`}
                      className="group bg-ink-card border border-white/5 hover:border-gold/40 p-6 transition-colors duration-300"
                    >
                      <span className="font-mono text-[10px] tracking-[0.25em] text-gold/70 uppercase">{r.type_label}</span>
                      <p className="mt-3 font-serif text-lg leading-snug group-hover:text-gold-light transition-colors">{r.title}</p>
                      <span className="mt-3 inline-block text-[10px] uppercase tracking-[0.2em] text-[#626773] group-hover:text-gold transition-colors">
                        Lire la suite
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-16 bg-ink-card border border-gold/20 p-8 sm:p-10 flex flex-wrap items-center justify-between gap-6" data-testid="fiche-cta">
              <div className="max-w-lg">
                <span className="label-accent">Une question sur ce sujet ?</span>
                <p className="mt-4 font-serif text-2xl leading-snug">
                  Votre situation mérite un{" "}
                  <em className="italic text-gold-gradient not-italic">conseil sur mesure.</em>
                </p>
                <p className="mt-3 text-sm text-[#A0A5B1] leading-relaxed">
                  Échangez avec un avocat du cabinet — vos échanges sont couverts
                  par le secret professionnel, premier échange sans engagement.
                </p>
              </div>
              <a
                href="/#contact"
                data-testid="fiche-cta-contact"
                className="inline-flex items-center gap-2 bg-gold text-ink text-xs uppercase tracking-[0.25em] font-medium px-8 py-4 hover:bg-gold-light transition-colors shrink-0"
              >
                Prendre rendez-vous
              </a>
            </div>

            {fiche.source_url && (
              <div className="mt-16 bg-ink-card border border-white/5 p-6 flex flex-wrap items-center justify-between gap-4">
                <p className="text-xs text-[#626773]">
                  Fiche migrée depuis le portail WordPress e².law.
                </p>
                <a
                  href={fiche.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="fiche-source-link"
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold/80 hover:text-gold transition-colors"
                >
                  Voir l'original <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            )}
          </motion.article>
        )}
      </div>

      {status === "ok" && fiche && (
        <motion.div
          className="fixed top-0 right-0 h-full z-40 flex items-center"
          animate={{ x: panelOpen ? 0 : 320 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
          <button
            onClick={() => setPanelOpen(!panelOpen)}
            data-testid="fiche-panel-toggle"
            aria-label="Ouvrir le panneau d'information"
            className="w-11 h-14 bg-gold text-ink flex items-center justify-center hover:bg-gold-light transition-colors shrink-0"
          >
            {panelOpen ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </button>

          <aside className="w-80 h-full glass border-l border-gold/15 p-8 overflow-y-auto" data-testid="fiche-side-panel">
            <div className="flex items-center gap-3">
              <Icon className="w-5 h-5 text-gold" />
              <span className="font-serif text-xl">{fiche.type_label}(s)</span>
            </div>
            {fiche.source_published && (
              <p className="mt-3 text-xs text-[#626773] font-mono tracking-wider">
                Publié le {new Date(fiche.source_published).toLocaleDateString("fr-BE", { day: "numeric", month: "long", year: "numeric" })}
              </p>
            )}

            <div className="mt-6 gold-hairline" />

            <div className="mt-8 border border-gold/25 bg-ink-card p-5" data-testid="fiche-disclaimer">
              <p className="text-xs text-[#A0A5B1] leading-relaxed">
                <span className="text-gold font-medium">Information générale.</span> Les
                informations de cette page ne tiennent pas compte de votre situation
                particulière et ne constituent ni une consultation juridique, ni un avis.
                Pour toute décision, consultez un avocat. En consultant cette page, vous
                acceptez nos{" "}
                <Link
                  to="/conditions-utilisation-portail"
                  data-testid="fiche-terms-link"
                  className="text-gold/90 underline decoration-gold/40 underline-offset-4 hover:text-gold"
                >
                  conditions d'utilisation de la base de connaissance
                </Link>
                .
              </p>
            </div>

            <form onSubmit={submitSearch} className="mt-8">
              <label htmlFor="panel-search" className="label-accent block mb-3">Recherche</label>
              <div className="flex">
                <input
                  id="panel-search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Rechercher une fiche…"
                  data-testid="panel-search-input"
                  className="flex-1 bg-transparent border border-white/15 focus:border-gold outline-none px-3 py-2.5 text-sm text-[#FAF8F5] placeholder:text-[#626773] transition-colors"
                />
                <button
                  type="submit"
                  data-testid="panel-search-button"
                  className="bg-gold text-ink px-4 hover:bg-gold-light transition-colors"
                  aria-label="Rechercher"
                >
                  <Search className="w-4 h-4" />
                </button>
              </div>
            </form>

            <div className="mt-8">
              <p className="label-accent mb-3">Catégories</p>
              {(fiche.categories || []).length > 0 ? (
                <div className="flex flex-wrap gap-2" data-testid="panel-categories">
                  {fiche.categories.map((c) => (
                    <Link
                      key={c}
                      to={`/ressources?cat=${encodeURIComponent(c)}`}
                      data-testid={`panel-cat-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="text-xs text-[#A0A5B1] border border-white/10 px-3 py-1.5 hover:border-gold/60 hover:text-gold transition-colors"
                    >
                      {c}
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-[#626773]">Non classifié(e)</p>
              )}
            </div>

            <div className="mt-8">
              <p className="label-accent mb-3">Étiquettes</p>
              {(fiche.tags || []).length > 0 ? (
                <div className="flex flex-wrap gap-2" data-testid="panel-tags">
                  {fiche.tags.map((t) => (
                    <Link
                      key={t}
                      to={`/ressources?tag=${encodeURIComponent(t)}`}
                      data-testid={`panel-tag-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="text-xs text-gold/80 border border-gold/25 px-3 py-1.5 hover:bg-gold hover:text-ink transition-colors"
                    >
                      {t}
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-[#626773]">Aucune étiquette</p>
              )}
            </div>
          </aside>
        </motion.div>
      )}
    </div>
  );
}
